import React, {
  Suspense,
  lazy,
  useEffect,
  createContext,
  useContext,
} from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import { initializeGA, trackPageView } from "./utils/analytics";
import { HelmetProvider } from "react-helmet-async";

// Contexto para manejar el estado de modales globalmente
export const ModalContext = createContext();
export const useModalContext = () => useContext(ModalContext);
const Header = lazy(() => import("./components/Header"));
const MobileNavBar = lazy(() => import("./components/MobileNavBar"));
const HeroSection = lazy(() => import("./components/HeroSection"));
const ProductsSection = lazy(() => import("./components/ProductsSection"));
const TestimonialsCarousel = lazy(
  () => import("./components/TestimonialsCarousel"),
);
const CorporateLogos = lazy(() => import("./components/CorporateLogos"));
const Footer = lazy(() => import("./components/Footer"));
const AboutPage = lazy(() => import("./components/AboutPage"));
const ProductsPage = lazy(() => import("./components/ProductsPage"));
const ContactPage = lazy(() => import("./components/ContactPage"));
const ReelsPage = lazy(() => import("./components/ReelsPage"));
const CalendarPage = lazy(() => import("./components/CalendarPage"));
const ChatBot = lazy(() => import("./components/ChatBot"));
const WhatsAppFloat = lazy(() => import("./components/WhatsAppFloat"));
const FAQSection = lazy(() => import("./components/FAQSection"));
const OtherProductsPage = lazy(() => import("./components/OtherProductsPage"));
const ProductCustomPage = lazy(() => import("./components/ProductCustomPage"));
const QuoteModal = lazy(() => import("./components/QuoteModal"));
const Cabina360 = lazy(() => import("./pages/productos/Cabina360"));
const CabinaEspejadaPage = lazy(
  () => import("./pages/productos/CabinaEspejada"),
);
const BarraEsquitesPage = lazy(() => import("./components/BarraEsquitesPage"));
const DJEventosPage = lazy(() => import("./components/DJEventosPage"));
const BlogPage = lazy(() => import("./components/BlogPage"));
const BlogPostPage = lazy(() => import("./components/BlogPostPage"));

const NeblinaBajaPage = lazy(() => import("./pages/productos/NeblinaBaja"));
const BarrasEventosPage = lazy(() => import("./components/BarrasEventosPage"));
const CanonConfetiPage = lazy(() => import("./components/CanonConfetiPage"));

// Landing pages SEO geolocalizadas
const Cabina360CDMX = lazy(() => import("./pages/landing/Cabina360CDMX"));
const Cabina360EDOMEX = lazy(() => import("./pages/landing/Cabina360EDOMEX"));
const Cabina360NorteEDOMEX = lazy(
  () => import("./pages/landing/Cabina360NorteEDOMEX"),
);

// Página de precios
const PreciosPage = lazy(() => import("./pages/PreciosPage"));

// Componente que sí puede usar useLocation (dentro del Router)
const AppContent = () => {
  const [isQuoteModalOpen, setIsQuoteModalOpen] = React.useState(false);
  const { hasOpenModal, setHasOpenModal } = useModalContext();
  const openQuoteModal = () => {
    setIsQuoteModalOpen(true);
    setHasOpenModal(true);
  };
  const closeQuoteModal = () => {
    setIsQuoteModalOpen(false);
    setHasOpenModal(false);
  };
  const setCurrentPage = () => {};
  const location = useLocation();
  useEffect(() => {
    initializeGA("G-F6NMDK6CWE");
  }, []);

  // Tracking de cambios de página (SPA)
  useEffect(() => {
    trackPageView(location.pathname, document.title);
  }, [location.pathname]);

  // Protección global de videos: ocultar descarga, prevenir PiP y menú contextual
  useEffect(() => {
    const preventContext = (e) => e.preventDefault();
    const protect = (video) => {
      try {
        video.setAttribute(
          "controlsList",
          "nodownload noplaybackrate noremoteplayback",
        );
        video.setAttribute("disablePictureInPicture", "");
        // Evitar duplicar listeners
        video.removeEventListener("contextmenu", preventContext);
        video.addEventListener("contextmenu", preventContext);
        video.classList.add("reels-video");
      } catch (_) {}
    };
    const protectAll = () => {
      document.querySelectorAll("video").forEach(protect);
    };
    // Proteger videos actuales
    protectAll();
    // Observar nuevos nodos
    const mo = new MutationObserver((mutations) => {
      for (const m of mutations) {
        m.addedNodes &&
          m.addedNodes.forEach((node) => {
            if (node.nodeType === 1) {
              if (node.tagName === "VIDEO") protect(node);
              node.querySelectorAll &&
                node.querySelectorAll("video").forEach(protect);
            }
          });
      }
    });
    mo.observe(document.body, { childList: true, subtree: true });
    return () => {
      mo.disconnect();
      // No removemos listeners para evitar flicker entre rutas; son idempotentes
    };
  }, []);

  // Re-aplicar protección tras cambios de ruta por contenido nuevo
  useEffect(() => {
    const protect = (video) => {
      try {
        video.setAttribute(
          "controlsList",
          "nodownload noplaybackrate noremoteplayback",
        );
        video.setAttribute("disablePictureInPicture", "");
        video.classList.add("reels-video");
      } catch (_) {}
    };
    document.querySelectorAll("video").forEach(protect);
  }, [location.pathname]);
  return (
    <Suspense
      fallback={
        <div className="w-full h-screen flex items-center justify-center text-xl text-purple-600">
          Cargando...
        </div>
      }
    >
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50">
        <Header />
        {/* Barra inferior fija tipo Instagram (solo móvil) */}
        <MobileNavBar />
        <Routes>
          <Route
            path="/"
            element={
              <>
                <HeroSection
                  openQuoteModal={openQuoteModal}
                  setCurrentPage={setCurrentPage}
                />
                <ProductsSection openQuoteModal={openQuoteModal} />
                <TestimonialsCarousel />
                <CorporateLogos />
                <FAQSection />
              </>
            }
          />
          <Route path="/about" element={<AboutPage />} />
          <Route
            path="/products"
            element={<ProductsPage openQuoteModal={openQuoteModal} />}
          />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/reels" element={<ReelsPage />} />
          <Route path="/calendar" element={<CalendarPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          <Route
            path="/other-products"
            element={<OtherProductsPage openQuoteModal={openQuoteModal} />}
          />
          <Route
            path="/producto-personalizado/:id"
            element={<ProductCustomPage />}
          />
          <Route path="/cabina-360" element={<Cabina360 />} />
          <Route path="/productos/cabina-360" element={<Cabina360 />} />
          <Route path="/cabina-espejada" element={<CabinaEspejadaPage />} />
          <Route path="/barra-esquites" element={<BarraEsquitesPage />} />
          <Route path="/dj-eventos" element={<DJEventosPage />} />
          <Route path="/neblina-baja" element={<NeblinaBajaPage />} />
          <Route path="/barras-eventos" element={<BarrasEventosPage />} />
          <Route path="/productos/neblina-baja" element={<NeblinaBajaPage />} />
          <Route
            path="/productos/cabina-espejada"
            element={<CabinaEspejadaPage />}
          />
          <Route
            path="/productos/canon-de-confeti"
            element={<CanonConfetiPage />}
          />

          {/* Landing pages SEO geolocalizadas */}
          <Route path="/cabina-360-cdmx" element={<Cabina360CDMX />} />
          <Route
            path="/cabina-360-ciudad-de-mexico"
            element={<Cabina360CDMX />}
          />
          <Route
            path="/cabina-360-estado-de-mexico"
            element={<Cabina360EDOMEX />}
          />
          <Route path="/cabina-360-edomex" element={<Cabina360EDOMEX />} />
          <Route path="/renta-cabina-360" element={<Cabina360CDMX />} />

          {/* Landing pages Norte EDOMEX - Tecámac, Ecatepec */}
          <Route
            path="/cabina-360-tecamac"
            element={<Cabina360NorteEDOMEX />}
          />
          <Route
            path="/cabina-360-ecatepec"
            element={<Cabina360NorteEDOMEX />}
          />
          <Route
            path="/cabina-360-coacalco"
            element={<Cabina360NorteEDOMEX />}
          />
          <Route
            path="/cabina-360-norte-edomex"
            element={<Cabina360NorteEDOMEX />}
          />
          <Route
            path="/cabina-360-los-heroes"
            element={<Cabina360NorteEDOMEX />}
          />

          {/* Página de precios */}
          <Route path="/precios" element={<PreciosPage />} />
          <Route path="/paquetes" element={<PreciosPage />} />

          <Route
            path="*"
            element={
              <>
                <HeroSection
                  openQuoteModal={openQuoteModal}
                  setCurrentPage={setCurrentPage}
                />
                <ProductsSection openQuoteModal={openQuoteModal} />
                <TestimonialsCarousel />
                <CorporateLogos />
                <FAQSection />
              </>
            }
          />
        </Routes>
        <Footer activeFooterTab={location.pathname} />
        <ChatBot isHidden={hasOpenModal} />
        <WhatsAppFloat isHidden={hasOpenModal} />
        {isQuoteModalOpen && (
          <QuoteModal isOpen={isQuoteModalOpen} onClose={closeQuoteModal} />
        )}
      </div>
    </Suspense>
  );
};

const App = () => {
  const [hasOpenModal, setHasOpenModal] = React.useState(false);

  return (
    <HelmetProvider>
      <ModalContext.Provider value={{ hasOpenModal, setHasOpenModal }}>
        <Router>
          <AppContent />
        </Router>
      </ModalContext.Provider>
    </HelmetProvider>
  );
};

export default App;
